import { useCallback, useEffect, useRef } from 'react';

// ==============================|| SHELL OFFSETS ||============================== //
//
// Every vertical offset in the shell lives here, and NONE of them is a written number any more.
//
// ⚠ WHAT WENT WRONG, AND WHY THE NUMBERS HAD TO GO. Berry reserves a fixed 88px (vertical menu) / 135px
// (horizontal) for the fixed AppBar and offsets the content column by exactly that. But the AppBar has no
// height of its own — it is as tall as whatever it contains. 88 was only ever the height that Berry's OWN
// toolbar happened to come out at (a 56px search input plus 2x16px padding). Anything that makes the row
// taller — a tenant logo, a browser font-size setting, a longer translation — pushes the AppBar past the
// reserve, and the reserve does not follow. The overflow lands on top of the first thing in the content
// column: the breadcrumb trail, then the page's first action button.
//
// That shipped, and it was found in the student app first: a tenant logo delivered as an SVG sized in
// MILLIMETRES (294mm x 145mm, i.e. an intrinsic 1112x547 CSS px) rendered ~112px tall in a 228px slot, so a
// ~160px AppBar sat over an 88px reserve and every page lost its breadcrumbs and the top of its primary
// button. The same tenant branding, the same Berry shell and the same Logo component are used here, so the
// same fix belongs here — the trigger is the TENANT, not the app.
//
// ⚠ SO THE HEIGHT IS MEASURED, NEVER WRITTEN. Measuring the AppBar covers every cause at once, including
// ones nobody has thought of yet, instead of chasing them one number at a time.
//
// ⚠ WHY CSS VARIABLES AND NOT REACT STATE. These are consumed by a `styled()` component and an `sx`.
// Threading a number through them re-renders the whole layout on every resize tick, and MainContentStyled
// would recompute its transitions mid-animation. A custom property is read by the browser at paint time, so
// the offsets follow the header with no React work at all.
//
// ⚠ EVERY FALLBACK IS BERRY'S ORIGINAL NUMBER. If nothing ever measures — jsdom, a browser with no
// ResizeObserver, a shell that fails to mount the ref — every calc() collapses to the layout Berry shipped
// with rather than to zero. A missing measurement degrades to "as before", never to "content at y=0".

/** Measured height of the whole fixed AppBar. */
export const APPBAR_HEIGHT_VAR = '--zc-appbar-h';

/** Measured height of the horizontal menu bar, when the horizontal menu orientation is in use. */
export const HBAR_HEIGHT_VAR = '--zc-hbar-h';

/** `var(--zc-appbar-h, 88px)` — the measured AppBar, falling back to Berry's original reserve. */
export const appBarHeight = `var(${APPBAR_HEIGHT_VAR}, 88px)`;

/** `var(--zc-hbar-h, 62px)` — the measured horizontal menu bar, falling back to Berry's original height. */
export const horizontalBarHeight = `var(${HBAR_HEIGHT_VAR}, 62px)`;

/**
 * Berry's minimum for the horizontal menu bar. It is a MIN, not a height: a nav label that wraps to two
 * lines used to overflow a fixed 62px box and paint over the page, because the bar is `position: fixed` at
 * a higher z-index than the content. It is now free to grow, and the content offset below follows it.
 */
export const HBAR_MIN_HEIGHT = 62;

/** The 2px seam Berry leaves between the horizontal menu bar and the content column (135 - 71 - 62). */
const HBAR_SEAM = 2;

/** Top offset for the content column under the vertical menu: the AppBar and nothing else. */
export const contentTopVertical = appBarHeight;

/**
 * Top offset for the content column under the horizontal menu: AppBar + menu bar + Berry's seam.
 *
 * ⚠ Unmeasured this sums to 152px, not Berry's original 135, because `appBarHeight` falls back to the
 * VERTICAL toolbar's 88px and Berry's horizontal toolbar is shorter (`p: 1.25`, ~71px). A custom property
 * gets one fallback, and this is the safe direction to miss in: an overshoot leaves a gap, an undershoot
 * puts the header back on top of the breadcrumbs. Horizontal mode always measures, so the live value is 135.
 */
export const contentTopHorizontal = `calc(${appBarHeight} + ${horizontalBarHeight} + ${HBAR_SEAM}px)`;

/**
 * A viewport-height remainder that accounts for the measured AppBar, e.g. `viewportBelowAppBar(40)` for a
 * box that also has to give back its own 40px of vertical padding.
 */
export function viewportBelowAppBar(extraPx = 0): string {
  return `calc(100vh - ${appBarHeight}${extraPx ? ` - ${extraPx}px` : ''})`;
}

/**
 * Publishes a measured height.
 *
 * ⚠ Set on `documentElement`, never on a layout node: MUI portals the drawer papers to `body`, so a variable
 * scoped to the layout subtree would not reach them.
 *
 * ⚠ Rounded. A fractional pixel in a calc() that also feeds a drawer height is how a 1px seam appears
 * between the header and the menu on fractional-DPI displays.
 */
function publish(varName: string, px: number): void {
  document.documentElement.style.setProperty(varName, `${Math.round(px)}px`);
}

/** Removes the property so consumers fall back to Berry's original number. */
function clear(varName: string): void {
  document.documentElement.style.removeProperty(varName);
}

/**
 * A callback ref that keeps `varName` equal to the node's measured height for as long as it is mounted.
 *
 * ⚠ A CALLBACK REF, not useRef + useEffect. The node exists by the time this runs, so the offset is right on
 * the FIRST paint — the shell is never briefly laid out for a header of a different size, which would show
 * as the page jumping a moment after it appears.
 *
 * ⚠ offsetHeight, NOT entry.contentRect. contentRect is the CONTENT box: it excludes padding and border, so
 * a header measured through it under-reports by its own padding and the content rides up over it. Both
 * paths here read the same number so they cannot drift apart.
 *
 * ⚠ The ResizeObserver is GUARDED, and not only for jsdom. If the API is missing the single mount-time
 * measurement above still stands, so the shell is correct for the height the header has at mount; what is
 * lost is tracking a later change. A stale-but-right-at-mount offset beats throwing inside a layout render,
 * which takes the whole shell down.
 */
export function useMeasuredHeightVar(varName: string): (node: HTMLElement | null) => void {
  const observerRef = useRef<ResizeObserver | null>(null);

  const ref = useCallback(
    (node: HTMLElement | null) => {
      observerRef.current?.disconnect();
      observerRef.current = null;

      if (!node) {
        clear(varName);
        return;
      }

      publish(varName, node.offsetHeight);

      if (typeof ResizeObserver === 'undefined') return;

      const observer = new ResizeObserver(() => publish(varName, node.offsetHeight));
      observer.observe(node);
      observerRef.current = observer;
    },
    [varName]
  );

  // Belt on the callback ref: React calls it with null on unmount, but a StrictMode double-invoke or a route
  // change that unmounts the layout could otherwise leave a stale offset behind.
  useEffect(
    () => () => {
      observerRef.current?.disconnect();
      clear(varName);
    },
    [varName]
  );

  return ref;
}
